// Favoritos e recentes do menu administrativo: apenas IDs (rotas) de ferramentas, isolados por instituição e usuário.
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

const EVT = 'dna:adm-nav-prefs';
const DEFAULT_FAVS = ['/v2/aluno'];
const MAX_RECENTS = 6;

const readUserId = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').id || 'anon'; } catch { return 'anon'; }
};
const keyFor = (uid) => `dnacore:adm-nav:v1:${window.location.hostname}:${uid}`;

function load(uid) {
  try {
    const raw = localStorage.getItem(keyFor(uid));
    const v = raw ? JSON.parse(raw) : {};
    return {
      favs: Array.isArray(v.favs) ? v.favs.filter((x) => typeof x === 'string') : DEFAULT_FAVS,
      recents: Array.isArray(v.recents) ? v.recents.filter((x) => typeof x === 'string') : [],
    };
  } catch { return { favs: DEFAULT_FAVS, recents: [] }; }
}

function save(uid, prefs) {
  try { localStorage.setItem(keyFor(uid), JSON.stringify(prefs)); } catch { /* armazenamento indisponível: mantém em memória */ }
  window.dispatchEvent(new CustomEvent(EVT, { detail: { uid, prefs } }));
}

// Limpa recentes do usuário atual (chamado no logout). Favoritos são preferência e permanecem por usuário.
export function clearAdminNavRecents() {
  const uid = readUserId();
  const p = load(uid);
  save(uid, { ...p, recents: [] });
}

export function useAdminNavPrefs() {
  const uid = readUserId();
  const [prefs, setPrefs] = useState(() => load(uid));

  useEffect(() => { setPrefs(load(uid)); }, [uid]);
  useEffect(() => {
    const on = (e) => { if (e.detail?.uid === uid) setPrefs(e.detail.prefs); };
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, [uid]);

  const update = useCallback((fn) => {
    const next = fn(load(uid));
    setPrefs(next);
    save(uid, next);
  }, [uid]);

  const toggleFav = useCallback((path) => update((p) => ({
    ...p, favs: p.favs.includes(path) ? p.favs.filter((x) => x !== path) : [...p.favs, path],
  })), [update]);

  const pushRecent = useCallback((path) => update((p) => (
    p.recents[0] === path ? p : { ...p, recents: [path, ...p.recents.filter((x) => x !== path)].slice(0, MAX_RECENTS) }
  )), [update]);

  return { favs: prefs.favs, recents: prefs.recents, toggleFav, pushRecent, isFav: (path) => prefs.favs.includes(path) };
}

// Presença do shell mobile administrativo: só o Layout2 (/v2) registra. Usado para suprimir o chrome mobile legado.
let shellCount = 0;
const listeners = new Set();
export const adminShellPresence = {
  enter() { shellCount += 1; listeners.forEach((l) => l()); },
  leave() { shellCount = Math.max(0, shellCount - 1); listeners.forEach((l) => l()); },
};
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const useAdminShellActive = () => useSyncExternalStore(subscribe, () => shellCount > 0);
